import React from "react";
import {
  MdCalendarToday,
  MdFolderOpen,
  MdOutlineDashboard,
  MdOutlineSecurity,
  MdSettings,
} from "react-icons/md";

/**
 * 側欄選單 — 逐字對應 DESIGN 稿 L60-L92 的 22 個項目與 5 個分組。
 */

/**
 * 品牌標記 — 生命週期環。
 *
 * EL PM 的定位是「從需求、規劃、開發、測試到交付的全生命週期管理」，
 * 所以用一個接近閉合的環（留一個缺口 + 箭頭）表示循環推進，
 * 中心一個實心點代表當下的專案。線條收在 2.2 粗、圓端點，在 20px 下仍清楚可辨。
 */
export const BrandMark = ({ size = 21 }) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {/* 環：缺口開在右上（-22°~58°），其餘為實線 */}
    <path
      d="M19.97 7.03A8.6 8.6 0 1 0 20.97 8.78"
      stroke="currentColor"
      strokeWidth="2.15"
      strokeLinecap="round"
    />
    {/* 箭頭：沿切線方向的實心三角，與 favicon / PWA 圖示同一個造型 */}
    <path d="M18.72 11.42 15.03 9.71l5.44-2.31z" fill="currentColor" />
    <circle cx="12" cy="12" r="2.7" fill="currentColor" />
  </svg>
);

/**
 * 選單結構：type "group" 為分組標題，其餘為連結。
 * key 對應設計稿的 page_route；path 為實際路由。
 *
 * permission ——「這一頁的 API 實際上要哪個模組權限」。
 *
 * 這裡的值必須跟後端各 Controller 的 [PermissionOnly("…")] 一字不差。
 * 之前是憑感覺借用相近的權限碼（績效/報表借 dashboard、風險借 project、
 * 使用者管理掛 setting_admin 但 AuthController 實際要的是 admin），
 * 結果選單看得到、按下去 403 —— 使用者會以為系統壞了，而不是自己沒權限。
 * 動這裡之前請先確認對應 Controller 上掛的是哪個模組碼。
 *
 * permission 為 null 代表「登入就能用」（後端該頁的端點沒有 PermissionOnly），
 * 不是「忘了填」。
 */
export const menus = [
  /*
   * 改版後側欄只剩 PRIMARY_NAV 那幾個入口，所以這份清單裡大半的項目都掛上了 hidden。
   * 理由跟底下「分析與治理」那段一模一樣：routePermissionOf 靠這份清單找權限碼，
   * 從 menus 刪掉會讓那些路由變成「對不到就放行」，反而比現在更沒防護。
   * 直接打網址仍然進得去（也仍然會被擋），只是側欄與命令面板上看不到。
   */
  { key: "dashboard", label: "儀表板", path: "/Dashboard", permission: "dashboard", hidden: true },
  // MyTaskController 沒有 PermissionOnly —— 看自己的工項不需要額外授權
  // 側欄的第七個入口（見 PRIMARY_NAV），所以不掛 hidden —— 命令面板也要找得到
  { key: "mytask", label: "個人工項", path: "/MyTask", permission: null },
  { key: "approvals", label: "待我審核", path: "/Approvals", permission: "task", badge: "approvals", hidden: true },

  { type: "group", label: "專案管理" },
  { key: "proj_list", label: "專案列表", path: "/Project/List", permission: "project" },

  { type: "group", label: "時程管理" },
  { key: "kanban", label: "看板", path: "/Board", permission: "task", hidden: true },
  { key: "sched_cal", label: "時程日曆", path: "/Schedule/Calendar", permission: "schedule", hidden: true },

  { type: "group", label: "分析與治理" },
  /*
   * hidden —— 暫時不出現在側欄、命令面板與登入著陸挑選，但頁面與路由都還在。
   *
   * 沒有直接從 menus 刪掉，是因為 routePermissionOf 要靠這份清單找對應的權限碼：
   * 刪掉會讓這幾條路由變成「對不到就放行」，反而比現在更沒防護。
   * 要恢復顯示就把 hidden 拿掉，不必動其他檔案。
   */
  { key: "perf_team", label: "團隊績效", path: "/Performance/Team", permission: "performance", hidden: true },
  { key: "perf_me", label: "個人績效", path: "/Performance/Report", permission: "performance", hidden: true },
  { key: "reports", label: "報表與分析", path: "/Reports", permission: "report", hidden: true },
  // 原碼檢測與弱點掃描是兩個獨立模組：弱點資料敏感、掃描吃資源，不跟 project 權限綁在一起
  { key: "codescan", label: "專案原碼檢測", path: "/CodeScan", permission: "codescan" },
  { key: "vulnscan", label: "專案弱點掃描", path: "/VulnScan", permission: "vulnscan" },
  { key: "timesheet", label: "每日工項紀錄", path: "/Timesheet", permission: "task", hidden: true },
  { key: "audit", label: "稽核紀錄", path: "/Audit", permission: "audit", hidden: true },

  { type: "group", label: "系統設定" },
  // 角色頁讀 /Auth/Role/List（role）、矩陣讀 /Auth/Role/Matrix（setting_role）；
  // 先擋最外層那支，進去矩陣拉不到時再由頁面自己處理
  { key: "set_role", label: "角色與權限", path: "/Setting/Roles", permission: "role" },
  { key: "set_admin", label: "使用者管理", path: "/Setting/Admin", permission: "admin" },
  { key: "automation", label: "自動化規則", path: "/Automation", permission: "setting", hidden: true },
  // 這是系統設定頁，掛 setting —— 原本掛 schedule，導致任何有時程檢視權的人（例如 RD）
  // 都會在「系統設定」底下看到它。HolidayController 的讀取端點已同步改成 setting。
  { key: "set_holiday", label: "國定假日", path: "/Setting/Holiday", permission: "setting", hidden: true },
  // 偏好設定人人都能改自己的；SMTP 區塊在頁面內另外用 setting.edit 擋
  { key: "set_pref", label: "偏好設定", path: "/Setting/Preferences", permission: null },

  /*
   * 改版新增的畫面。
   *
   * 全部 hidden —— 它們的入口在 PRIMARY_NAV 或各自的上層畫面裡，
   * 不該再從側欄多冒出一份。列在這裡純粹是為了讓 routePermissionOf 找得到權限碼：
   * 例如 /Task/12 要有 task 權限、/Project/Import/3 要有 project 權限，
   * 沒有這幾列的話這些新網址會變成「對不到就放行」。
   *
   * permission 為 null 同樣代表「登入就能用」（後端該頁端點沒有 PermissionOnly）。
   */
  { key: "tasks", label: "工項", path: "/Tasks", permission: "task", hidden: true },
  { key: "task_new", label: "新增工項", path: "/Task/New", permission: "task", hidden: true },
  { key: "task_detail", label: "工項詳情", path: "/Task", permission: "task", hidden: true },
  { key: "sched_goals", label: "里程碑", path: "/Schedule", permission: "schedule", hidden: true },
  { key: "sched_week", label: "本週到期項目", path: "/Schedule/Week", permission: "schedule", hidden: true },
  { key: "goal_new", label: "新增里程碑", path: "/Schedule/Goal/New", permission: "schedule", hidden: true },
  { key: "goal_plan", label: "工項排程", path: "/Schedule/Goal/Plan", permission: "schedule", hidden: true },
  // 檢測入口本身不綁權限：頁面內的原碼檢測／弱點掃描兩區各自要 codescan / vulnscan
  { key: "scan", label: "檢測", path: "/Scan", permission: null, hidden: true },
  { key: "notify", label: "通知", path: "/Notifications", permission: null, hidden: true },
  { key: "search", label: "搜尋", path: "/Search", permission: null, hidden: true },
  { key: "me", label: "我的帳號", path: "/Me", permission: null, hidden: true },
  { key: "me_pwd", label: "變更密碼", path: "/Me/Password", permission: null, hidden: true },
  { key: "guide", label: "新手指南", path: "/Guide", permission: null, hidden: true },
  { key: "map", label: "畫面索引", path: "/Map", permission: null, hidden: true },
  // 新增／編輯人員跟使用者管理同一個模組碼：AuthController 上掛的是 admin
  { key: "set_admin_edit", label: "編輯人員", path: "/Setting/Admin/New", permission: "admin", hidden: true },
  { key: "proj_import", label: "從 Excel 匯入工項", path: "/Project/Import", permission: "project", hidden: true },
];

/** header 頁面標題 — 逐字對應設計稿 titles map */
export const PAGE_TITLES = {
  dashboard: "儀表板",
  mytask: "個人工項",
  approvals: "審核",
  proj_list: "專案列表",
  proj_detail: "專案詳情",
  proj_add: "新增專案",
  kanban: "看板",
  sched_cal: "時程日曆",
  perf_team: "團隊績效",
  perf_me: "個人績效",
  reports: "報表與分析",
  codescan: "專案原碼檢測",
  vulnscan: "專案弱點掃描",
  timesheet: "每日工項紀錄",
  audit: "稽核紀錄",
  set_role: "角色與權限",
  set_admin: "使用者管理",
  automation: "自動化規則",
  set_holiday: "國定假日與行事曆",
  set_pref: "偏好設定",

  /*
   * 改版新畫面的標題 —— 逐字照抄設計稿 el-pm-template_4.html:2653-2676 的 TITLES。
   *
   * 設計稿刻意讓同一個入口底下的子畫面共用同一個標題（工項清單與工項詳情都叫「工項」、
   * 里程碑／週視圖／排程都叫「時程」），差別寫在副標。標題跟著子畫面一直變的話，
   * 使用者會以為自己被丟到別的模組去了。副標由各畫面自己給，這裡只存標題。
   */
  tasks: "工項",
  task_new: "新增工項",
  task_detail: "工項",
  sched_goals: "時程",
  sched_week: "時程",
  goal_new: "時程",
  goal_plan: "時程",
  scan: "檢測",
  setting: "設定",
  search: "搜尋",
  notify: "通知",
  me: "我的帳號",
  me_pwd: "我的帳號",
  guide: "新手指南",
  map: "畫面索引",
  account: "帳號與角色",
  set_admin_edit: "帳號與角色",
  proj_import: "專案",
};

/**
 * 路由 path → 設計稿 route key（用於 header 標題與導覽步驟查找）。
 *
 * **順序就是語意**：查表用的是 startsWith，而且取第一個命中的，
 * 所以「長的路徑一定要排在短的前面」。排錯不是顯示問題，是權限問題 ——
 * routePermissionOf 走的是同一張表，"/Tasks" 若排在 "/Task" 後面就會被 "/Task" 吃掉，
 * 整個跨專案工項清單會套到工項詳情的權限碼上，張冠李戴且很難從畫面上看出來。
 *
 * 底下刻意照「路徑前綴的群組」排，同一組內由深到淺，新增時請沿用這個排法。
 */
export const ROUTE_KEYS = [
  // /Project —— Import 比 Detail 長，先排（兩者雖然不互為前綴，仍維持由深到淺的一致性）
  ["/Project/Import", "proj_import"],
  ["/Project/Detail", "proj_detail"],
  ["/Project/Add", "proj_add"],
  ["/Project/List", "proj_list"],

  // /Schedule —— 四條子路徑都必須排在裸的 /Schedule（里程碑頁）前面
  ["/Schedule/Calendar", "sched_cal"],
  ["/Schedule/Week", "sched_week"],
  ["/Schedule/Goal/New", "goal_new"],
  ["/Schedule/Goal/Plan", "goal_plan"],
  ["/Schedule", "sched_goals"],

  ["/Performance/Team", "perf_team"],
  ["/Performance/Report", "perf_me"],

  // /Setting —— Roles/Matrix 與 Admin/New 是各自上層的前綴延伸，必須先排
  ["/Setting/Roles/Matrix", "set_role"],
  ["/Setting/Roles", "set_role"],
  ["/Setting/Admin/New", "set_admin_edit"],
  ["/Setting/Admin", "set_admin"],
  ["/Setting/Holiday", "set_holiday"],
  ["/Setting/Preferences", "set_pref"],

  // /Tasks 與 /Task 只差一個 s，"/Tasks".startsWith("/Task") 為真 —— 順序寫反就全錯
  ["/Tasks", "tasks"],
  ["/Task/New", "task_new"],
  ["/Task", "task_detail"],

  ["/Me/Password", "me_pwd"],
  ["/Me", "me"],

  ["/Dashboard", "dashboard"],
  ["/MyTask", "mytask"],
  ["/Approvals", "approvals"],
  ["/Board", "kanban"],
  ["/Reports", "reports"],
  ["/CodeScan", "codescan"],
  ["/VulnScan", "vulnscan"],
  ["/Scan", "scan"],
  ["/Notifications", "notify"],
  ["/Search", "search"],
  ["/Guide", "guide"],
  ["/Map", "map"],
  ["/Timesheet", "timesheet"],
  ["/Audit", "audit"],
  ["/Automation", "automation"],
];

export function routeKeyOf(pathname) {
  const hit = ROUTE_KEYS.find(([p]) => pathname.startsWith(p));
  return hit ? hit[1] : "dashboard";
}

/**
 * 側邊欄入口 —— 前六個逐字取自設計稿 el-pm-template_4.html:458-463，
 * 第七個（個人工項）是刻意加回來的，理由寫在那一列旁邊。
 *
 * 設計稿的主張是「六個入口就是全系統的入口」（我們多留了一個）：舊側欄 22 列、5 個分組，
 * 使用者得先學會分類才找得到東西；現在只剩六個，其餘畫面都從入口底下進去。
 *
 * icon 是 react-icons 的元件。bundle.css 的 .el-nav__icon 是 font-size: var(--icon)，
 * react-icons 預設尺寸是 1em，所以大字模式下會跟著放大。
 *
 * label 一律有字：規則第 3 條，只有圖示的按鈕不算數。
 *
 * permission 為 null 代表登入即可；「檢測」另外用 anyPermission 表達
 * 「codescan 或 vulnscan 其中之一就顯示」—— 這兩個模組是分開授權的，
 * 只有弱點掃描權的人照樣該看得到這個入口。
 */
export const PRIMARY_NAV = [
  /*
   * 個人工項＝首頁。原本的「今天」頁跟這一頁讀同一支 /MyTask/List、列同一批工項，
   * 使用者要先看「今天」再點進來才能回報，等於多一站；所以合併：
   * 「今天」唯一獨有的「待我審核」改成這一頁最上方的摘要列，待審核紅點也掛到這裡。
   * /Today 舊網址由 Router 導到 /MyTask。
   *
   * 圖示用半圓：這一頁的重點是「做到幾成」。
   */
  { key: "mytask", label: "個人工項", path: "/MyTask", icon: <MdOutlineDashboard />, permission: null, badge: "approvals" },
  { key: "projects", label: "專案", path: "/Project/List", icon: <MdFolderOpen />, permission: "project" },
  { key: "schedule", label: "時程", path: "/Schedule", icon: <MdCalendarToday />, permission: "schedule" },
  { key: "scan", label: "檢測", path: "/Scan", icon: <MdOutlineSecurity />, permission: null, anyPermission: ["codescan", "vulnscan"] },
  { key: "setting", label: "設定", path: "/Setting", icon: <MdSettings />, permission: null },
];

/**
 * 畫面 key → 側欄入口的 key —— 分組照抄設計稿 el-pm-template_4.html:2678-2687。
 *
 * 用途是算側欄的 aria-current：進到子畫面（工項詳情、排里程碑、編輯人員…）時，
 * 對應的入口仍然要是亮的。沒有這張表就得靠 path 前綴猜，而好幾個子畫面的網址
 * 根本不在入口的前綴底下（看板 /Board 掛在「專案」、審核 /Approvals 掛在「今天」）。
 *
 * 少數 key 是設計稿沒有、但我們還留著的舊畫面（mytask / sched_cal / codescan / vulnscan），
 * 照它們的實際歸屬補上；查不到的 key 就是「不屬於任何入口」，所有入口都不亮 ——
 * 那是誠實的結果，不要為了讓它亮而硬塞一個入口。
 */
export const NAV_OF = {
  /* 原本掛在「今天」底下的子畫面，合併後改掛「個人工項」 */
  notify: "mytask",
  approvals: "mytask",
  search: "mytask",

  /* 個人工項現在是自己的入口（見 PRIMARY_NAV 的說明），不再掛在「今天」底下 */
  mytask: "mytask",

  proj_list: "projects",
  proj_add: "projects",
  proj_detail: "projects",
  proj_import: "projects",
  kanban: "projects",

  /*
   * 獨立的「工項」清單拿掉了（跨專案看全部工項改成從專案點進去，看自己的走個人工項），
   * 所以工項詳情與新增工項的側邊欄高亮改掛在「專案」—— 工項本來就住在專案底下。
   * /Tasks 舊網址在 Router 導去個人工項，這裡跟著掛 mytask，高亮才對得上落地的那一頁。
   */
  tasks: "mytask",
  task_new: "projects",
  task_detail: "projects",

  sched_goals: "schedule",
  goal_new: "schedule",
  goal_plan: "schedule",
  sched_week: "schedule",
  sched_cal: "schedule",

  scan: "scan",
  codescan: "scan",
  vulnscan: "scan",

  setting: "setting",
  set_pref: "setting",
  me: "setting",
  me_pwd: "setting",
  guide: "setting",
  set_admin: "setting",
  set_admin_edit: "setting",
  set_role: "setting",
};

/** 權限碼 → 中文模組名（與後端 AuthService.MatrixModules 同一份措辭） */
export const MODULE_NAMES = {
  dashboard: "儀表板",
  project: "專案",
  task: "工項",
  schedule: "時程",
  performance: "績效",
  report: "報表",
  risk: "風險",
  codescan: "原碼檢測",
  vulnscan: "弱點掃描",
  audit: "稽核",
  setting: "系統設定",
  role: "角色管理",
  admin: "使用者管理",
};

/**
 * 側欄上沒有、但仍屬於某個模組的頁面。
 * 專案詳情與新增專案不是選單項目，卻同樣要有專案權限才進得去。
 */
const EXTRA_ROUTE_PERMISSION = {
  proj_detail: "project",
  proj_add: "project",
};

/**
 * 路由 path → 進入該頁所需的權限碼（null = 登入即可，undefined 的路由不擋）。
 *
 * 側欄靠 menus 決定「顯示什麼」，但那只是視覺上的隱藏 —— 直接打網址原本照樣進得去，
 * 畫面會停在一片空白或紅字錯誤（後端 [PermissionOnly] 回 403），使用者會以為系統壞了。
 * 讓路由本身用同一份 menus 判斷，兩邊就不可能不一致。
 *
 * 對不到 ROUTE_KEYS 的路徑一律放行，交給後端把關：這裡多擋一個未知路由的風險，
 * 大於少擋一個的風險。
 */
export function routePermissionOf(pathname) {
  const hit = ROUTE_KEYS.find(([p]) => pathname.startsWith(p));
  if (!hit) return null;
  const key = hit[1];
  if (EXTRA_ROUTE_PERMISSION[key]) return EXTRA_ROUTE_PERMISSION[key];
  const item = menus.find((m) => m.key === key);
  return item ? item.permission || null : null;
}
